<script lang="ts">
  import { setContext, onMount } from "svelte"
  import Layout from "$app/Layouts/Plan.svelte"
  import PlanInfo from "./PlanInfo.svelte"
  import EditDepartments from "./EditDepartments.svelte"
  import PlanMembers from "./PlanMembers.svelte"
  import EmailSettings from "./EmailSettings.svelte"
  import Archive from "./Archive.svelte"
  import { appStore } from "$app/store.svelte"
  import { Cog } from "lucide-svelte"

  interface Props {
    plan: PlanType
    current_user: UserType
    subscription: SubscriptionType
    users: any[]
    logo_name: string
    market_segments: { id: number; name: string }[]
    time_zones: any[]
  }

  let {
    plan,
    current_user,
    subscription,
    users = $bindable(),
    logo_name,
    market_segments,
    time_zones,
    ...rest
  }: Props = $props()

  appStore.plan = plan
  appStore.current_user = current_user
  appStore.subscription = subscription

  setContext("usersHash", users)

  users = Object.values(users).filter(
    (u: UserType) => !u.deleted && u.role !== "coach"
  )
  users = users.map((u) => {
    return { ...u, edit: false, showDetail: false }
  })
  appStore.users = users

  const hostname = location.hostname.substring(
    0,
    location.hostname.indexOf(".")
  )

  type tabType = {
    title: string
    active: boolean
    enabled: boolean
    slug: string
  }
  let tabs = $state([
    {
      title: "Company",
      active: true,
      enabled: true,
      component: PlanInfo,
      slug: "company"
    },
    {
      title: "Departments",
      active: false,
      enabled: true,
      component: EditDepartments,
      slug: "departments"
    },
    {
      title: "Members",
      active: false,
      enabled: true,
      component: PlanMembers,
      slug: "members"
    },
    {
      title: "Emails",
      active: false,
      enabled: true,
      component: EmailSettings,
      slug: "emails"
    },
    {
      title: "Archive",
      active: false,
      enabled: true,
      component: Archive,
      slug: "archive"
    }
  ])

  let activeTabSlug = $state(tabs.find((v) => v.active)?.slug)

  function updateTab(tab: tabType): void {
    if (tab.enabled) {
      const activeTab = tabs.find((t) => t.active === true)
      activeTab.active = false
      tab.active = true
      tabs = tabs
    }
  }

  onMount(() => {
    var hash = window.location.hash.substr(1)
    if (hash) {
      const tab = tabs.find((t) => t.slug.replace(/\s+/g, "") === hash)
      if (tab) {
        updateTab(tab)
      }
    }
  })
</script>

<Layout {plan} {current_user} {...rest}>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="max-w-5xl mx-auto pb-6">
      <div class="w-full py-6 flex items-center justify-center">
        <Cog class="w-8 h-8 text-gray-600 mr-2" />
        <p class="text-3xl text-center text-bold text-gray-700">
          Plan Settings
        </p>
      </div>

      <div class="overflow-hidden">
        <div class="sm:hidden">
          <label for="tabs" class="sr-only">Select a tab</label>
          <select
            id="tabs"
            name="tabs"
            class="block w-full focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
            bind:value={activeTabSlug}
            onchange={() => {
              updateTab(tabs.find((t) => t.slug === activeTabSlug))
            }}
          >
            {#each tabs.filter((t) => t.enabled) as tab (tab.slug)}
              <option>{tab.title}</option>
            {/each}
          </select>
        </div>
        <div class="bg-white hidden sm:block">
          <div class="border-b border-gray-200">
            <nav class="tabs -mb-px flex justify-around" aria-label="Tabs">
              {#each tabs.filter((t) => t.enabled) as tab (tab.slug)}
                <a
                  href="#{tab.slug}"
                  class="tab"
                  class:active={tab.active}
                  onclick={() => updateTab(tab)}
                >
                  {tab.title}
                </a>
              {/each}
            </nav>
          </div>
        </div>
        <div class="bg-white px-4 py-5 sm:p-6 mt-6 md:mt-0">
          <div
            class="bg-white px-4 py-5 sm:p-6 mt-6 md:mt-0"
            class:hidden={!tabs[0].active}
          >
            <PlanInfo {market_segments} {logo_name} {time_zones} />
          </div>
          <div class:hidden={!tabs[1].active}>
            <EditDepartments />
          </div>

          <div class:hidden={!tabs[2].active}>
            <PlanMembers />
          </div>
          <div class:hidden={!tabs[3].active}>
            <EmailSettings />
          </div>

          <div class:hidden={!tabs[4].active}>
            <Archive />
          </div>
        </div>
      </div>
    </div>
  </div>
</Layout>
